import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/api";
import { useTranslation } from "react-i18next";
import { useCurrency } from "@/context/CurrencyContext";
import { LanguageCurrencySwitcher } from "@/components/LanguageCurrencySwitcher";

const TYPES_STRUCTURE = [
  "Indépendant / Micro-entrepreneur", "Artisan", "TPE", "PME", "Franchise",
  "Association / ONG", "Syndicat", "Camping / Hébergement", "Éducateur canin",
  "Photographe / Créatif", "Établissement scolaire", "Autre",
];

// Fourchettes de budget en EUR (source de vérité) — affichées converties dans la devise choisie (Phase 81)
const BUDGETS = [
  { key: "top", min: 100, max: 290, tier: "TOP BUDGET" },
  { key: "medium", min: 290, max: 790, tier: "MEDIUM BUDGET" },
  { key: "standard", min: 790, max: 990, tier: "STANDARD BUDGET" },
  { key: "maximum", min: 1000, tier: "MAXIMUM BUDGET" },
  { key: "conseil" },
];

const VALID_BUDGET_KEYS = BUDGETS.map((b) => b.key);
const NO_FRAC = { minFrac: 0, maxFrac: 0 };

function BudgetLabel({ b }) {
  const { format, formatEur, isEur } = useCurrency();
  if (b.key === "conseil") return <>Je souhaite être conseillé</>;
  const yearly = b.max != null ? `${format(b.min, "EUR", NO_FRAC)} – ${format(b.max, "EUR", NO_FRAC)}/an` : `${format(b.min, "EUR", NO_FRAC)}/an et +`;
  const monthly = b.max != null ? `≈ ${format(b.min / 12, "EUR", NO_FRAC)} – ${format(b.max / 12, "EUR", NO_FRAC)}/mois` : `≈ ${format(b.min / 12, "EUR", NO_FRAC)}+/mois`;
  const eur = b.max != null ? `${formatEur(b.min)} – ${formatEur(b.max)}/an` : `${formatEur(b.min)}/an et +`;
  return (
    <>
      <span data-testid={`dev-budget-label-${b.key}`}>{`${yearly}  (${monthly}) — ${b.tier}`}</span>
      {!isEur && <span className="block text-[10px] font-normal text-[#868E96] mt-0.5" data-testid={`dev-budget-eur-${b.key}`}>{`(≈ ${eur})`}</span>}
    </>
  );
}

export default function Devis() {
  const [params] = useSearchParams();
  const { i18n } = useTranslation();
  const { currency } = useCurrency();
  // Lit ?budget=top|medium|standard|maximum|conseil (envoyé par la Landing) et
  // pré-sélectionne le bouton correspondant, sinon fallback sur "conseil".
  const initialBudget = React.useMemo(() => {
    const raw = (params.get("budget") || params.get("tier") || "").toLowerCase();
    return VALID_BUDGET_KEYS.includes(raw) ? raw : "conseil";
  }, [params]);

  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    raison_sociale: "", nom: "", prenom: "", email: "", telephone: "",
    type_demande: "sur_mesure_v2",
    siret: "", type_structure: "", activite: "",
    nb_salaries: "", nb_etablissements: "",
    budget_range: initialBudget, paiement_souhaite: "les_deux",
    besoin_detail: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        nb_salaries: form.nb_salaries ? parseInt(form.nb_salaries) : null,
        nb_etablissements: form.nb_etablissements ? parseInt(form.nb_etablissements) : null,
        message: form.besoin_detail || form.message,
        language: (i18n.resolvedLanguage || "fr").split("-")[0],
        currency,
      };
      await api.post("/public/devis", payload);
      setSubmitted(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  if (submitted) return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="border-b border-[#DEE2E6]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3"><img src="/pag-logo.png" alt="PAG" className="h-10 w-10" /><span className="font-display font-black text-xl">PAG</span></Link>
        </div>
      </header>
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="max-w-xl w-full bg-[#059669] text-white p-10 border border-[#0A0A0A]" data-testid="devis-success">
          <CheckCircle2 className="h-12 w-12 text-[#FFD700] mb-4" />
          <h1 className="font-display text-3xl lg:text-4xl font-black tracking-tighter mb-4">Demande envoyée.</h1>
          <p className="text-white/85">Notre équipe commerciale analyse votre besoin et vous recontacte sous <b>48h ouvrées</b> avec un devis sur mesure et les modules adaptés à votre activité.</p>
          <Link to="/" className="inline-block mt-6 underline font-semibold">Retour à l&apos;accueil →</Link>
        </div>
      </main>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-[#DEE2E6]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3"><img src="/pag-logo.png" alt="PAG" className="h-10 w-10" /><span className="font-display font-black text-xl">PAG</span></Link>
          <div className="flex items-center gap-3">
            <LanguageCurrencySwitcher compact />
            <Link to="/" className="text-sm font-semibold flex items-center gap-2 hover:text-[#002FA7]"><ArrowLeft className="h-4 w-4" /> Accueil</Link>
          </div>
        </div>
      </header>

      <section className="border-b border-[#DEE2E6] bg-[#FFD700]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
          <div className="text-xs uppercase tracking-[0.3em] font-semibold mb-3">// Devis sur mesure</div>
          <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tighter">Demandez votre devis sur mesure.</h1>
          <p className="mt-3 text-base text-[#0A0A0A]/80 max-w-2xl">
            Indiquez-nous votre activité, votre budget et vos besoins. Nous vous proposons la formule adaptée avec les modules dont vous avez réellement besoin.
          </p>
        </div>
      </section>

      <main className="max-w-3xl mx-auto p-6 lg:p-12">
        <div className="mb-6 p-4 border-l-4 border-[#FFD700] bg-[#FFF9E6]" data-testid="devis-info-banner">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#0A0A0A] mb-1">// À savoir</div>
          <div className="text-sm text-[#0A0A0A]">
            <b>Ce formulaire est une demande commerciale.</b> Il ne crée pas de compte automatiquement. Nous vous
            répondrons par email avec une proposition personnalisée et un lien de paiement.
            <br />
            <b>Pour un accès immédiat</b> à votre espace : <Link to="/inscription" className="underline font-bold hover:text-[#002FA7]">souscrivez directement</Link> — paiement en ligne, compte créé instantanément après paiement.
          </div>
        </div>
        <form onSubmit={submit} className="space-y-6" data-testid="devis-form">
          <Section title="1. Votre structure">
            <F label="Nom / Raison sociale" value={form.raison_sociale} onChange={(v) => setForm({ ...form, raison_sociale: v })} required testid="dev-rs" />
            <div className="grid grid-cols-2 gap-3">
              <F label="SIRET" value={form.siret} onChange={(v) => setForm({ ...form, siret: v })} testid="dev-siret" />
              <F label="Nombre de salariés" type="number" value={form.nb_salaries} onChange={(v) => setForm({ ...form, nb_salaries: v })} testid="dev-nbs" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Type de structure</Label>
              <Select value={form.type_structure} onValueChange={(v) => setForm({ ...form, type_structure: v })}>
                <SelectTrigger data-testid="dev-type-struct" className="h-10"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                <SelectContent>
                  {TYPES_STRUCTURE.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <F label="Activité principale" value={form.activite} onChange={(v) => setForm({ ...form, activite: v })} testid="dev-activite" placeholder="Ex : Éducateur canin, Restauration rapide, Salon coiffure…" />
          </Section>

          <Section title="2. Contact">
            <div className="grid grid-cols-2 gap-3">
              <F label="Nom" value={form.nom} onChange={(v) => setForm({ ...form, nom: v })} required testid="dev-nom" />
              <F label="Prénom" value={form.prenom} onChange={(v) => setForm({ ...form, prenom: v })} required testid="dev-prenom" />
              <F label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testid="dev-email" />
              <F label="Téléphone" type="tel" value={form.telephone} onChange={(v) => setForm({ ...form, telephone: v })} required testid="dev-tel" />
            </div>
          </Section>

          <Section title="3. Budget & paiement">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Budget cible</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                {BUDGETS.map((b) => (
                  <button
                    key={b.key}
                    type="button"
                    onClick={() => setForm({ ...form, budget_range: b.key })}
                    data-testid={`dev-budget-${b.key}`}
                    className={`p-3 border-2 text-left text-sm font-semibold transition-colors ${form.budget_range === b.key ? "border-[#002FA7] bg-[#002FA7]/5" : "border-[#DEE2E6] hover:border-[#0A0A0A]"}`}
                  >
                    <BudgetLabel b={b} />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Paiement souhaité</Label>
              <Select value={form.paiement_souhaite} onValueChange={(v) => setForm({ ...form, paiement_souhaite: v })}>
                <SelectTrigger data-testid="dev-paiement" className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="mensuel">Mensuel</SelectItem>
                  <SelectItem value="annuel">Annuel (engagement 1 an)</SelectItem>
                  <SelectItem value="les_deux">Peu importe, je choisirai plus tard</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <F label="Nombre d'établissements (si multi-sites)" type="number" value={form.nb_etablissements} onChange={(v) => setForm({ ...form, nb_etablissements: v })} testid="dev-nbe" />
          </Section>

          <Section title="4. Votre besoin">
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Que souhaitez-vous gérer avec PAG Business Manager ? *</Label>
              <Textarea
                value={form.besoin_detail}
                onChange={(e) => setForm({ ...form, besoin_detail: e.target.value })}
                rows={6}
                required
                data-testid="dev-besoin"
                placeholder="Ex : Je gère un cabinet vétérinaire avec 3 employés. J'ai besoin de rendez-vous, dossiers clients (avec animaux), devis, factures et bulletins de paie…"
              />
            </div>
          </Section>

          <Button type="submit" disabled={submitting} data-testid="dev-submit" className="w-full h-12 bg-[#002FA7] hover:bg-[#002175] text-white font-semibold">
            {submitting ? "Envoi…" : "Demander mon devis sur mesure"} <ArrowUpRight className="ml-2 h-4 w-4" />
          </Button>
          <p className="text-xs text-[#868E96] text-center">
            Notre équipe commerciale analyse votre demande et vous recontacte sous 48h ouvrées.
          </p>
        </form>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white border border-[#DEE2E6] p-6 space-y-3">
      <div className="text-xs uppercase tracking-[0.25em] font-bold mb-3 flex items-center gap-2">
        <span className="h-px bg-[#0A0A0A] w-6" />
        {title}
      </div>
      {children}
    </div>
  );
}

function F({ label, value, onChange, type = "text", required, testid, placeholder }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">{label}{required && <span className="text-[#E60000]"> *</span>}</Label>
      <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} data-testid={testid} placeholder={placeholder} className="h-10" />
    </div>
  );
}
